import React from "react";
import { useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import Cookies from "js-cookie";
import { toast } from "react-toastify";

import { useAuth } from "../../context/AuthProvider";
import { parseJwt } from "../../utils";
import { getFromLS, setToLS } from "../../utils/storage";
// import { initializeGlobalOptions } from "../utils/globalInitializer";

import {
  CygnusLogin,
  SignInResult,
  VerifyOtpResult,
} from "@cygnusaiinternal/cygnus-common";

const NewLoginPage: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();

  const handleSuccessfulLogin = async (data: any): Promise<void> => {
    // All localStorage stays here
    localStorage.setItem("token", data.Token);
    const firmId = parseJwt(data.Token).FirmId;
    localStorage.setItem("firmId", firmId);
    localStorage.setItem("username", data.Username);
    localStorage.setItem("usertype", data.UserType);
    localStorage.setItem("firmname", data.FirmName);
    localStorage.setItem("sendto", data.SendTo);
    localStorage.setItem("defaultActiveKey", "clients");
    localStorage.setItem("firstname", data.FirstName);
    localStorage.setItem("lastname", data.LastName);
    localStorage.setItem("firmImg", data.FirmLogoLink);
    localStorage.setItem("userId", data.FirmDetailId)
    if (auth) { auth.login(data.Username); }

    // Load active features
    const payload = { Username: data.Username };
    const requestOptions: RequestInit = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(payload),
    };

    const resp = await fetch(
      `${process.env.REACT_APP_BASE_URI}/workflow/subscriptions/features/`,
      requestOptions
    );
    const result = await resp.json();

    setToLS("active-features", result.data.FeatureCode);

    if (getFromLS("active-features").length > 0) {
      navigate("/dashboard", { replace: true });
    }
  };

  const onSignIn = async ({
    username,
    password,
  }: {
    username: string | undefined;
    password: string;
  }): Promise<SignInResult> => {
    const url = `${process.env.REACT_APP_BASE_URI}/workflow/authenticate/`;
    const hashedPassword = CryptoJS.SHA256(password).toString();

    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify({
          Username: username,
          Password: hashedPassword,
          ProductCode: "ENGAGE360",
        }),
        headers: { "Content-type": "application/json; charset=UTF-8" },
      });

      const data = await response.json();

      if (data.success === false) {
        toast.error(data.message, {
          position: "bottom-right",
          autoClose: 2500,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          className: "toastify-color-error",
        });
        return { success: false, message: data.message };
      }

      const requiresPasswordChange =
      data.RequiresPasswordChange === true || data.RequiresPasswordChange === "Y";

      if (requiresPasswordChange) {
        // DO NOT store token. DO NOT initialize. DO NOT request OTP.
        return {
          success: true,
          requiresPasswordChange: true,
          username: data.Username,
          message: "Password change required.",
        };
      }

      // Store token & basic info now (needed for OTP APIs too)
      localStorage.setItem("token", data.Token);
      const firmId = parseJwt(data.Token).FirmId;
      localStorage.setItem("firmId", firmId);
      localStorage.setItem("username", data.Username);
      localStorage.setItem("usertype", data.UserType);
      localStorage.setItem("firmname", data.FirmName);
      localStorage.setItem("sendto", data.SendTo);
      localStorage.setItem("defaultActiveKey", "clients");
      localStorage.setItem("firstname", data.FirstName);
      localStorage.setItem("lastname", data.LastName);
      localStorage.setItem("firmImg", data.FirmLogoLink);
      localStorage.setItem("userId", data.FirmDetailId)

      // If MFA not required – do everything here and tell UI "no OTP".
      if (data.isMFARequired === "N") {
        // await initializeGlobalOptions();
        await handleSuccessfulLogin(data);
        return { success: true, requiresOtp: false };
      }

      // MFA required: replicate your cookie logic and request OTP here
      const savedUsername = Cookies.get("username");
      const savedPassword = Cookies.get("password");

      const sameUser = savedUsername && savedUsername === username;
      const samePassword = savedPassword && savedPassword === password;

      if (sameUser && samePassword) {
        // bypass OTP
        // await initializeGlobalOptions();
        await handleSuccessfulLogin(data);
        return { success: true, requiresOtp: false };
      }

      // Request OTP
      const otpUrl = `${process.env.REACT_APP_BASE_URI}/workflow/request_otp/`;
      const otpResp = await fetch(otpUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${data.Token}`,
        },
        body: JSON.stringify({ Username: username }),
      });
      const otpData = await otpResp.json();

      if (!otpData.success) {
        toast.error(otpData.message || "Failed to request OTP");
        return { success: false, message: otpData.message };
      }

      return { success: true, requiresOtp: true };
    } catch (err) {
      console.error("Error during sign in:", err);
      toast.error("Something went wrong. Please try again.");
      return { success: false, message: "Network error" };
    }
  };

  const onVerifyOtp = async ({
    username,
    password,
    otp,
    rememberMe,
  }: {
    username: string | undefined;
    password: string;
    otp: string;
    rememberMe: boolean;
  }): Promise<VerifyOtpResult> => {
    const url = `${process.env.REACT_APP_BASE_URI}/workflow/verify_otp/`;

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ Username: username, otp }),
      });

      if (response.status === 403) {
        const data = await response.json();
        toast.error(data.message || "Invalid OTP. Please try again.");
        // You were reloading the page earlier – do it here if you still want that:
        // window.location.reload();
        return { success: false, message: data.message };
      }

      const data = await response.json();

      if (!data.success) {
        toast.error(data.message || "Invalid OTP");
        return { success: false, message: data.message };
      }

      // Remember me cookies
      if (rememberMe && username) {
        Cookies.set("username", username, { expires: 7 });
        Cookies.set("password", password, { expires: 7 });
      } else {
        Cookies.remove("username");
        Cookies.remove("password");
      }

      // Finish login: global options + features + navigation
      // await initializeGlobalOptions();
      await handleSuccessfulLogin({
        Token: localStorage.getItem("token"),
        Username: username,
        UserType: localStorage.getItem("usertype"),
        FirmName: localStorage.getItem("firmname"),
        SendTo: localStorage.getItem("sendto"),
        FirstName: localStorage.getItem("firstname"),
        LastName: localStorage.getItem("lastname"),
        FirmLogoLink: localStorage.getItem("firmImg"),
        FirmDetailId: localStorage.getItem("userId"),
      });
      return { success: true };
    } catch (err) {
      console.error("Error verifying OTP:", err);
      toast.error("Something went wrong while verifying OTP.");
      return { success: false, message: "Network error" };
    }
  };

  return (
    <CygnusLogin
      onSignIn={onSignIn}
      onVerifyOtp={onVerifyOtp}
      baseUri={process.env.REACT_APP_BASE_URI}
      onSSOComplete={(username: string) => {
        auth?.login(username);
        navigate("/dashboard", { replace: true });
        localStorage.setItem("defaultActiveKey", "clients");
      }}
      onLoginComplete={() => {
        // Navigation is handled inside handleSuccessfulLogin.
      }}
      onForgotPasswordClick={(params?: { username?: string | undefined }) => {
        navigate("/forgot-password", { state: { username: params?.username ?? "" } });
      }}
      onTermsClick={() => navigate("/terms-of-service")}
      titleText="Cygnus Accounting Hub"
      subtitleText="Integrations made easy"
    />
  );
};

export default NewLoginPage;
